import {
  mergeResourceSchedules,
  collectScheduleHours,
  getScheduleMaxTimes,
  resolveCalendarTotals,
} from '@/helpers/scheduleHelper';
import { toStartOfDayIso } from '@/helpers/dateHelper';
import type {
  AvailabilityCalendarResponse,
  LocalizedPriceSummary,
  ResourceScheduleDay,
  ResourceScheduleSlot,
} from '@/services/naaviApi/types';

export function cleanCalendarParams(params: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null) {
      cleaned[key] = value;
    }
  });
  return cleaned;
}

export function buildEmptySchedule(baseDate: Date, days: number): ResourceScheduleDay[] {
  if (!(baseDate instanceof Date) || Number.isNaN(baseDate.getTime())) {
    return [];
  }
  const totalDays = Number.isFinite(Number(days)) ? Number(days) : 0;
  if (totalDays <= 0) {
    return [];
  }
  const schedule: ResourceScheduleDay[] = [];
  for (let i = 0; i < totalDays; i += 1) {
    const nextDate = new Date(baseDate.getTime());
    nextDate.setDate(baseDate.getDate() + i);
    const year = nextDate.getFullYear();
    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const day = String(nextDate.getDate()).padStart(2, '0');
    schedule.push({
      date: `${year}-${month}-${day}`,
      times: [],
      slots: [] as ResourceScheduleSlot[],
    });
  }
  return schedule;
}

function alignScheduleWindow(
  schedule: ResourceScheduleDay[] | null | undefined,
  baseDate: Date,
  days: number,
): ResourceScheduleDay[] {
  const windowSchedule = buildEmptySchedule(baseDate, days);
  if (!windowSchedule.length) {
    return Array.isArray(schedule) ? schedule : [];
  }

  const scheduleByDate = new Map<string, ResourceScheduleDay>();
  if (Array.isArray(schedule)) {
    schedule.forEach((day) => {
      if (day && typeof day.date === 'string') {
        scheduleByDate.set(day.date, day);
      }
    });
  }

  return windowSchedule.map((day) => {
    const existing = scheduleByDate.get(day.date);
    if (!existing) {
      return day;
    }
    const merged = mergeResourceSchedules([day], [existing]);
    return merged.length ? merged[0] : day;
  });
}

type CalendarNormalizeOptions = {
  response: AvailabilityCalendarResponse | { data?: AvailabilityCalendarResponse } | null;
  previousSchedule?: ResourceScheduleDay[] | null;
  merge?: boolean;
  fallbackBaseDate?: Date | null;
  fallbackDays?: number;
  fallbackBaseDateIso?: string | null;
  currentPriceLocalized?: LocalizedPriceSummary | null;
  currentPriceLabel?: string | null;
};

function resolveCalendarPayload(
  response: AvailabilityCalendarResponse | { data?: AvailabilityCalendarResponse } | null,
): AvailabilityCalendarResponse | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  if ('schedule' in response && 'totals' in response) {
    return response as AvailabilityCalendarResponse;
  }

  if ('data' in response) {
    const payload = (response as { data?: AvailabilityCalendarResponse }).data;
    if (payload && 'schedule' in payload && 'totals' in payload) {
      return payload;
    }
  }

  return null;
}

export function normalizeCalendarResponse(options: CalendarNormalizeOptions) {
  const responseData = resolveCalendarPayload(options.response);
  const rawSchedule: ResourceScheduleDay[] = responseData && Array.isArray(responseData.schedule)
    ? responseData.schedule
    : [];

  let incomingSchedule = rawSchedule;
  const fallbackDays = Number(options.fallbackDays) || 0;
  if (options.fallbackBaseDate instanceof Date && fallbackDays > 0) {
    incomingSchedule = alignScheduleWindow(incomingSchedule, options.fallbackBaseDate, fallbackDays);
  } else if (!incomingSchedule.length && options.fallbackBaseDate instanceof Date) {
    const fallback = buildEmptySchedule(options.fallbackBaseDate, fallbackDays);
    if (fallback.length) {
      incomingSchedule = fallback;
    }
  }

  const previousSchedule: ResourceScheduleDay[] = Array.isArray(options.previousSchedule)
    ? options.previousSchedule
    : [];
  const shouldMerge = options.merge === true;
  const nextSchedule = shouldMerge
    ? mergeResourceSchedules(previousSchedule, incomingSchedule)
    : incomingSchedule;

  const hours = collectScheduleHours(nextSchedule);
  const scheduleMaxTimes = getScheduleMaxTimes(nextSchedule);
  const totals = resolveCalendarTotals(responseData, nextSchedule);

  const bookingOption = responseData && responseData.bookingOption ? responseData.bookingOption : null;
  const calendarWindow = responseData && responseData.window ? responseData.window : null;
  const priceLocalized = bookingOption && bookingOption.priceLocalized !== undefined && bookingOption.priceLocalized !== null
    ? bookingOption.priceLocalized
    : (options.currentPriceLocalized !== undefined && options.currentPriceLocalized !== null ? options.currentPriceLocalized : null);

  let priceLabel: string | null = null;
  if (bookingOption && bookingOption.priceDisplay !== undefined && bookingOption.priceDisplay !== null) {
    priceLabel = String(bookingOption.priceDisplay);
  } else if (priceLocalized && priceLocalized.grossAmountStr !== undefined && priceLocalized.grossAmountStr !== null) {
    priceLabel = String(priceLocalized.grossAmountStr);
  } else if (options.currentPriceLabel !== undefined && options.currentPriceLabel !== null) {
    priceLabel = String(options.currentPriceLabel);
  }

  const windowStart = nextSchedule.length ? nextSchedule[0].date : null;
  let baseDateIso = options.fallbackBaseDateIso !== undefined && options.fallbackBaseDateIso !== null
    ? options.fallbackBaseDateIso
    : null;
  let windowDate = null;
  if (windowStart) {
    baseDateIso = toStartOfDayIso(`${windowStart}T00:00:00`);
    const candidate = new Date(`${windowStart}T00:00:00`);
    if (!Number.isNaN(candidate.getTime())) {
      windowDate = candidate;
    }
  } else if (!baseDateIso && options.fallbackBaseDate instanceof Date && !Number.isNaN(options.fallbackBaseDate.getTime())) {
    baseDateIso = toStartOfDayIso(options.fallbackBaseDate);
    windowDate = new Date(options.fallbackBaseDate.getTime());
  }

  return {
    schedule: nextSchedule,
    hours,
    scheduleMaxTimes,
    totals,
    priceLocalized,
    priceLabel,
    baseDateIso,
    windowDate,
    window: calendarWindow,
  };
}
